import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, Eye, AlertCircle, RotateCcw, ZoomIn, ZoomOut, FileText, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { cvsAPI, downloadFile } from '../utils/api';

const CVPreview = ({ cv, isOpen, onClose }) => {
  const { t, currentLanguage } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [docxHtml, setDocxHtml] = useState('');
  const [docxWarnings, setDocxWarnings] = useState([]);
  const [isRetrying, setIsRetrying] = useState(false);
  const fileName = (cv?.original_name || '').toLowerCase();
  const isPdf = cv?.mime_type === 'application/pdf' || fileName.endsWith('.pdf');
  const isDocx = cv?.mime_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    || fileName.endsWith('.docx');
  const isDoc = cv?.mime_type === 'application/msword' || fileName.endsWith('.doc');

  // Détecter si PDF.js est disponible
  const [pdfJsLoaded, setPdfJsLoaded] = useState(false);

  const sanitizeDocxHtml = useCallback((rawHtml) => {
    if (!rawHtml || typeof window === 'undefined') {
      return rawHtml || '';
    }

    const parser = new window.DOMParser();
    const doc = parser.parseFromString(rawHtml, 'text/html');

    doc.querySelectorAll('script, iframe, object, embed').forEach((node) => node.remove());

    doc.querySelectorAll('*').forEach((node) => {
      [...node.attributes].forEach((attribute) => {
        const name = attribute.name.toLowerCase();
        const value = (attribute.value || '').trim().toLowerCase();
        const scriptProtocol = `java${'script:'}`;
        if (name.startsWith('on') || ((name === 'href' || name === 'src') && value.startsWith(scriptProtocol))) {
          node.removeAttribute(attribute.name);
        }
      });
    });

    return doc.body.innerHTML || '';
  }, []);

  useEffect(() => {
    // Import dynamique de PDF.js pour éviter les erreurs SSR
    const loadPdfJs = async () => {
      try {
        const pdfjsLib = await import('pdfjs-dist');
        
        // Configuration PDF.js avec worker local intégré
        if (typeof window !== 'undefined') {
          // Tester l'accès au worker local
          const workerUrl = '/pdf.worker.min.js';
          
          try {
            // Vérifier que le worker est accessible
            const testResponse = await fetch(workerUrl, { method: 'HEAD' });
            if (testResponse.ok) {
              pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
              // PDF.js configuré avec worker local
            } else {
              throw new Error(`Worker inaccessible: ${testResponse.status}`);
            }
          } catch (workerError) {
            // Worker local inaccessible, fallback sans worker
            // Fallback: désactiver le worker
            pdfjsLib.disableWorker = true;
          }
        }
        
        setPdfJsLoaded(true);
        return pdfjsLib;
      } catch (error) {
        // Erreur chargement PDF.js
        setError(t('cvPreview.errors.pdfJsUnavailable', 'PDF.js non disponible'));
        setLoading(false);
        return null;
      }
    };

    if (isOpen && cv && isPdf) {
      loadPdfJs();
    } else if (isOpen && cv && !isPdf) {
      setPdfJsLoaded(false);
    }
  }, [isOpen, cv, isPdf, t]);

  useEffect(() => {
    if (!isOpen || !cv) return;
    setError(null);
    setPdfDoc(null);
    setDocxHtml('');
    setDocxWarnings([]);
    setNumPages(null);
    setPageNumber(1);
    setScale(1.0);
    if (!isPdf && !isDocx) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [isOpen, cv, isPdf, isDocx]);

  // Fonction pour charger le PDF
  const loadPdf = useCallback(async () => {
    if (!pdfJsLoaded || !cv || !isPdf) return;

    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const previewUrl = `/api/cv/${cv.id}/file?disposition=inline`;
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Charger PDF.js dynamiquement
      const pdfjsLib = await import('pdfjs-dist');
      
      // Charger le document PDF avec configuration professionnelle
      const loadingTask = pdfjsLib.getDocument({
        url: previewUrl,
        httpHeaders: authHeaders,
        withCredentials: false,
        isEvalSupported: false,
        disableAutoFetch: false,
        disableStream: false,
        useWorkerFetch: true,
        disableRange: false,
        cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/cmaps/`,
        cMapPacked: true,
        standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/standard_fonts/`,
        verbosity: 0 // Réduire les logs en production
      });

      // Gestion des progrès de chargement
      loadingTask.onProgress = (progress) => {
        if (progress.total > 0) {
          // Chargement PDF: progress
        }
      };

      const pdf = await loadingTask.promise;
      
      setPdfDoc(pdf);
      setNumPages(pdf.numPages);
      setPageNumber(1);
      setLoading(false);
      
      // PDF chargé avec succès
      
    } catch (error) {
      // Erreur chargement PDF
      
      // Gestion d'erreur spécialisée selon le type d'erreur
      let errorMessage = t('cvPreview.errors.loadFailed', 'Impossible de charger le PDF');
      
      if (error.name === 'MissingPDFException') {
        errorMessage = t('cvPreview.errors.pdfCorrupted', 'Le fichier PDF est corrompu ou invalide');
      } else if (error.name === 'UnexpectedResponseException') {
        errorMessage = t('cvPreview.errors.network');
      } else if (error.name === 'PasswordException') {
        errorMessage = t('cvPreview.errors.passwordProtected');
      } else if (error.message.includes('worker')) {
        errorMessage = t('cvPreview.errors.configuration');
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = t('cvPreview.errors.fileAccess');
      } else {
        errorMessage = t('cvPreview.errors.generic', {
          message: error.message || t('cvPreview.errors.loadFailed', 'Impossible de charger le PDF')
        });
      }
      
      setError(errorMessage);
      setLoading(false);
      setPdfDoc(null);
    }
  }, [cv, pdfJsLoaded, isPdf, t]);

  const loadDocxPreview = useCallback(async () => {
    if (!cv || !isDocx) return;

    try {
      setLoading(true);
      setError(null);

      const response = await cvsAPI.getPreviewHtml(cv.id);
      const rawHtml = response?.data?.html || '';
      setDocxHtml(sanitizeDocxHtml(rawHtml));
      setDocxWarnings(Array.isArray(response?.data?.warnings) ? response.data.warnings : []);
      setLoading(false);
    } catch (previewError) {
      setError(
        t('cvPreview.errors.generic', {
          message: previewError?.response?.data?.error || t('cvPreview.errors.loadFailed', 'Impossible de charger le CV')
        })
      );
      setLoading(false);
    }
  }, [cv, isDocx, sanitizeDocxHtml, t]);

  // Charger le PDF quand le composant s'ouvre
  useEffect(() => {
    if (isOpen && cv && pdfJsLoaded && isPdf) {
      loadPdf();
    }
  }, [isOpen, cv, pdfJsLoaded, isPdf, loadPdf]);

  // Charger l'aperçu DOCX (HTML)
  useEffect(() => {
    if (isOpen && cv && isDocx) {
      loadDocxPreview();
    }
  }, [isOpen, cv, isDocx, loadDocxPreview]);

  // Retry function
  const retryLoad = async () => {
    setIsRetrying(true);
    if (isDocx) {
      await loadDocxPreview();
    } else {
      await loadPdf();
    }
    setIsRetrying(false);
  };

  // Render page
  const renderPage = useCallback(async (pageNum) => {
    if (!pdfDoc) return null;
    
    try {
      const page = await pdfDoc.getPage(pageNum);
      const canvas = document.getElementById(`pdf-page-${pageNum}`);
      if (!canvas) return;
      
      const context = canvas.getContext('2d');
      const viewport = page.getViewport({ scale });
      
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
    } catch (error) {
      // Erreur rendu page
    }
  }, [pdfDoc, scale]);

  // Re-render current page when scale changes
  useEffect(() => {
    if (pdfDoc && pageNumber) {
      renderPage(pageNumber);
    }
  }, [pdfDoc, pageNumber, scale, renderPage]);

  if (!isOpen || !cv) return null;

  const downloadCV = async () => {
    try {
      const response = await cvsAPI.getFile(cv.id, 'attachment');
      downloadFile(response.data, cv.original_name || 'cv');
    } catch (error) {
      setError(t('cvPreview.errors.downloadFailed'));
    }
  };

  const goToPrevPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const goToNextPage = () => {
    if (pageNumber < numPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const zoomIn = () => {
    setScale(prevScale => Math.min(prevScale + 0.2, 3.0));
  };

  const zoomOut = () => {
    setScale(prevScale => Math.max(prevScale - 0.2, 0.5));
  };

  const resetZoom = () => {
    setScale(1.0);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-7xl h-[98vh] sm:h-[95vh] flex flex-col shadow-2xl border border-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200 bg-white flex-shrink-0">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
            <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="text-sm sm:text-lg font-semibold text-gray-900 truncate">
                {t('cvPreview.title')}
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 truncate">
                {cv.original_name} • {cv.file_size ? `${Math.round(cv.file_size / 1024)} KB` : 'Taille inconnue'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Zoom Controls */}
            {pdfDoc && (
              <div className="flex items-center space-x-1 text-sm text-gray-600">
                <button
                  onClick={zoomOut}
                  className="p-1 bg-gray-100 rounded hover:bg-gray-200"
                  title={t('cvPreview.zoomOut')}
                  aria-label={t('cvPreview.zoomOut')}
                >
                  <ZoomOut className="h-4 w-4" />
                </button>
                <span className="px-2">{Math.round(scale * 100)}%</span>
                <button
                  onClick={zoomIn}
                  className="p-1 bg-gray-100 rounded hover:bg-gray-200"
                  title={t('cvPreview.zoomIn')}
                  aria-label={t('cvPreview.zoomIn')}
                >
                  <ZoomIn className="h-4 w-4" />
                </button>
                <button
                  onClick={resetZoom}
                  className="p-1 bg-gray-100 rounded hover:bg-gray-200"
                  title={t('cvPreview.resetZoom')}
                  aria-label={t('cvPreview.resetZoom')}
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              </div>
            )}
            
            {/* Page Navigation */}
            {numPages && (
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <button
                  onClick={goToPrevPage}
                  disabled={pageNumber <= 1}
                  className="px-2 py-1 bg-gray-100 rounded disabled:opacity-50 hover:bg-gray-200"
                  aria-label={t('common.previous')}
                >
                  ←
                </button>
                <span>
                  {pageNumber} / {numPages}
                </span>
                <button
                  onClick={goToNextPage}
                  disabled={pageNumber >= numPages}
                  className="px-2 py-1 bg-gray-100 rounded disabled:opacity-50 hover:bg-gray-200"
                  aria-label={t('common.next')}
                >
                  →
                </button>
              </div>
            )}
            
            <button
              onClick={downloadCV}
              className="inline-flex items-center px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Download className="h-4 w-4 mr-1" />
              {t('cvs.actions.download')}
            </button>
            
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
              aria-label={t('common.close')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-center justify-center bg-gray-50 relative overflow-auto">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-500">
                  {isRetrying
                    ? t('cvPreview.retrying', 'Nouvelle tentative...')
                    : isDocx
                      ? t('cvPreview.loadingDocx', 'Chargement du document DOCX...')
                      : t('cvPreview.loadingPdf', 'Chargement du PDF...')
                  }
                </p>
              </div>
            </div>
          )}
          
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
              <div className="text-center p-6 max-w-md">
                <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">{error}</p>
                <div className="space-x-3">
                  <button
                    onClick={retryLoad}
                    disabled={isRetrying}
                    className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    {t('cvPreview.retry', 'Réessayer')}
                  </button>
                  <button
                    onClick={downloadCV}
                    className="inline-flex items-center px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {t('cvPreview.downloadCta', 'Télécharger le CV')}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {isPdf && pdfDoc && !loading && !error && (
            <div className="w-full h-full overflow-auto flex items-start justify-center p-2 sm:p-4">
              <div className="bg-white shadow-lg rounded-lg" style={{ 
                transform: `scale(${scale})`,
                transformOrigin: 'top center',
                transition: 'transform 0.2s ease-in-out'
              }}>
                <canvas
                  id={`pdf-page-${pageNumber}`}
                  className="block"
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              </div>
            </div>
          )}

          {isDocx && !loading && !error && (
            <div className="w-full h-full p-3 sm:p-6 bg-gray-50 overflow-y-auto">
              <div className="bg-white rounded-lg p-6 shadow-sm max-w-4xl mx-auto">
                {docxWarnings.length > 0 && (
                  <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    {t('cvPreview.docxFormattingNotice', 'Certaines mises en forme DOCX peuvent être simplifiées dans l’aperçu.')}
                  </div>
                )}
                <div
                  className="prose prose-sm sm:prose lg:prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: docxHtml || '<p></p>' }}
                />
              </div>
            </div>
          )}

          {isDoc && (
            <div className="w-full h-full p-6 bg-gray-50 overflow-y-auto">
              <div className="bg-white rounded-lg p-6 shadow-sm text-center">
                <FileText className="h-16 w-16 text-orange-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">
                  {t('cvPreview.oldDocFormat')}
                </h3>
                <p className="text-gray-600 mt-2 mb-6">
                  {t('cvPreview.oldDocNotSupported')}
                </p>
                <button
                  onClick={downloadCV}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('cvPreview.downloadFile')}
                </button>
              </div>
            </div>
          )}

          {!isPdf && !isDocx && !isDoc && (
            <div className="w-full h-full p-6 bg-gray-50 overflow-y-auto">
              <div className="bg-white rounded-lg p-6 shadow-sm text-center">
                <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">
                  {t('cvPreview.unsupportedFormat')}
                </h3>
                <p className="text-gray-600 mt-2">
                  {t('cvPreview.unsupportedMessage')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-3 sm:px-4 py-2 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-between text-xs sm:text-sm text-gray-500">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <span>Format: {isPdf ? 'PDF' : isDocx ? 'DOCX' : isDoc ? 'DOC' : 'Autre'}</span>
              {cv.created_at && (
                <span className="hidden sm:inline">{t('cvPreview.addedOn')} {new Date(cv.created_at).toLocaleDateString(currentLanguage === 'fr' ? 'fr-FR' : currentLanguage === 'es' ? 'es-ES' : 'en-US')}</span>
              )}
              {isPdf && numPages && (
                <span>{numPages} page{numPages > 1 ? 's' : ''}</span>
              )}
            </div>
            <div className="text-xs text-gray-400 hidden sm:block">
              {isPdf ? t('cvPreview.poweredBy') : ''}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CVPreview;
